import { useRef, useEffect } from "react";

// Lightweight 3D-feel particle network rendered on a canvas.
// Depth is simulated via z-based sizing, parallax on mouse move, and connecting lines.
export default function ParticleField() {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();

    const count = Math.min(240, Math.floor((w * h) / 7000));
    const particles = Array.from({ length: count }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      z: Math.random(),
      vx: (Math.random() - 0.5) * 0.28,
      vy: (Math.random() - 0.5) * 0.28,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const px = mouse.current.active ? (mouse.current.x - w / 2) / w : 0;
      const py = mouse.current.active ? (mouse.current.y - h / 2) / h : 0;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        const depth = 0.4 + p.z * 1.6;
        const ox = px * depth * 40;
        const oy = py * depth * 40;
        const sx = p.x + ox;
        const sy = p.y + oy;
        const r = 1.4 + p.z * 4.2;

        const grad = ctx.createLinearGradient(sx - r, sy, sx + r, sy);
        grad.addColorStop(0, "rgba(0,240,255,0.95)");
        grad.addColorStop(1, "rgba(176,38,255,0.95)");
        ctx.beginPath();
        ctx.fillStyle = grad;
        ctx.globalAlpha = 0.45 + p.z * 0.5;
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const qx = q.x + px * (0.4 + q.z * 1.6) * 40;
          const qy = q.y + py * (0.4 + q.z * 1.6) * 40;
          const dx = sx - qx;
          const dy = sy - qy;
          const dist = Math.hypot(dx, dy);
          if (dist < 190) {
            ctx.beginPath();
            ctx.globalAlpha = (1 - dist / 190) * 0.28;
            ctx.strokeStyle = "#6b8dff";
            ctx.lineWidth = 1;
            ctx.moveTo(sx, sy);
            ctx.lineTo(qx, qy);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };

    if (!reduce) draw();
    else {
      // static single frame
      draw();
      cancelAnimationFrame(raf);
    }

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top, active: true };
    };
    const onLeave = () => (mouse.current.active = false);
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="particle-field-canvas"
      className="absolute inset-0 h-full w-full"
    />
  );
}
